import { QueryClient } from "@tanstack/react-query";
import { createTRPCReact } from "@trpc/react-query";
import { httpBatchLink, httpLink, loggerLink } from "@trpc/client";
import type { AppRouter } from "@quire/trpc";
import { runtimeConfig } from "./runtime-config";

import { authApi } from "./auth-api";

export const trpc = createTRPCReact<AppRouter>();

function getBaseUrl(): string {
  return runtimeConfig.apiUrl;
}

let refreshPromise: Promise<any> | null = null;

async function fetchWithAuthRefresh(
  url: RequestInfo | URL,
  options?: RequestInit
): Promise<Response> {
  const res = await fetch(url, {
    ...options,
    credentials: "include"
  });

  if (res.status === 401) {
    const urlStr = url.toString();
    if (urlStr.includes("/api/auth/refresh")) {
      return res;
    }

    if (!refreshPromise) {
      refreshPromise = authApi.refreshSession()
        .then((r) => {
          refreshPromise = null;
          return r;
        })
        .catch(() => {
          refreshPromise = null;
          return null;
        });
    }

    const refreshResult = await refreshPromise;
    if (refreshResult && refreshResult.data?.user) {
      return fetch(url, {
        ...options,
        credentials: "include"
      });
    }
  }

  return res;
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 120_000,
      gcTime: 15 * 60_000,
      retry: 1,
      refetchOnWindowFocus: false
    }
  }
});

export const trpcClient = trpc.createClient({
  links: [
    loggerLink({
      colorMode: "none",
      enabled: () => import.meta.env.DEV
    }),
    (import.meta.env.DEV && import.meta.env.VITE_TRPC_BATCH !== "1"
      ? httpLink({
          url: `${getBaseUrl()}/trpc`,
          fetch: fetchWithAuthRefresh,
          headers() {
            return { "x-trpc-source": "dashboard" };
          }
        })
      : httpBatchLink({
          url: `${getBaseUrl()}/trpc`,
          fetch: fetchWithAuthRefresh,
          headers() {
            return { "x-trpc-source": "dashboard" };
          }
        }))
  ]
});
